import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CancelIcon,
  CheckCircleIcon,
  CheckIcon,
  ContentCopyIcon,
  ErrorOutlineIcon,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  canAdminCancelAllocation,
} from '@billda/design-system';
import { PermissionGate } from '../../../../../shared/components/gates/PermissionGate';
import { Permissions, usePermission } from '@billda/permissions-ui';
import { FeatureSwitchGate } from '../../../../../shared/components/gates/FeatureSwitchGate';
import { Features } from '@billda/features-ui';
import { useBookingDetails } from '../../../hooks/useBookingDetails';
import { useVariation } from '../../../hooks/useVariation';
import { useAllocationEventsTimeline } from '../../../hooks/useAllocationEventsTimeline';
import { useVariationEventsTimeline } from '../../../hooks/useVariationEventsTimeline';
import { formatCurrency } from '../../../../../shared/utils/currency';
import { formatDate } from '../../../../../shared/utils/dateFormatting';
import { BookingImageGallery } from '../../../../bookings/components/BookingImageGallery';
import { VariationStatusBadge } from '../atoms/VariationStatusBadge';
import { VariationImageGallery } from './VariationImageGallery';
import {
  AllocationReviewPanelContent,
  type AllocationReviewMode,
} from './AllocationReviewPanelContent';
import {
  BookingActivitySection,
  BookingClaimSection,
  BookingContextSection,
  BookingDecisionOutcome,
  BookingEvidenceSection,
  BookingHeader,
  BookingSection,
} from './booking-details/BookingPanelSections';
import { InfoRow } from '../atoms/InfoRow';

const EMPTY_ALLOCATION_ID = '00000000-0000-0000-0000-000000000000';

interface CommonBookingDetailsPanelProps {
  onReviewSuccess?: () => void;
  potStatus?: string;
}

type BookingDetailsPanelProps = CommonBookingDetailsPanelProps &
  (
    | { allocationId: string; variationId?: never }
    | { allocationId?: never; variationId: string }
  );

export function BookingDetailsPanel(props: BookingDetailsPanelProps) {
  const requestedVariationId = props.variationId ?? null;
  const {
    data: variation,
    error: variationError,
    isLoading: variationLoading,
  } = useVariation(requestedVariationId);
  const variationAllocationId = variation?.allocation.id;
  const resolvedAllocationId =
    props.allocationId ??
    (variationAllocationId && variationAllocationId !== EMPTY_ALLOCATION_ID
      ? variationAllocationId
      : null);
  const {
    data: booking,
    error: bookingError,
    isLoading: bookingLoading,
  } = useBookingDetails(resolvedAllocationId);
  const allocationTimeline = useAllocationEventsTimeline(
    booking?.plot.siteId ?? null,
    booking?.plot.plotId ?? null,
    resolvedAllocationId
  );
  const variationTimeline = useVariationEventsTimeline(
    variation?.plotContext.siteId ?? null,
    variation?.plotContext.plotId ?? null,
    requestedVariationId && !resolvedAllocationId ? requestedVariationId : null
  );
  const canApprove = usePermission(Permissions.Labour.Approve);
  const canCancel = usePermission(Permissions.Allocations.Cancel);
  const canAdminCancel = usePermission(Permissions.Allocations.Admin.Cancel);
  const [reviewMode, setReviewMode] = useState<AllocationReviewMode | null>(
    null
  );
  const [reviewReturnsToChooser, setReviewReturnsToChooser] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>(
    'idle'
  );

  useEffect(() => {
    setReviewMode(null);
    setReviewReturnsToChooser(false);
  }, [resolvedAllocationId, requestedVariationId]);

  const handleCopy = useCallback(async (text: string) => {
    setCopyState('idle');
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2_000);
    } catch {
      setCopyState('error');
      setTimeout(() => setCopyState('idle'), 2_000);
    }
  }, []);

  if (
    (requestedVariationId && variationError && !variation) ||
    (resolvedAllocationId && bookingError && !booking)
  ) {
    const error = variationError ?? bookingError;
    return (
      <div data-testid="booking-details-panel" className="p-6">
        <Alert severity="error">
          {error instanceof Error
            ? error.message
            : 'Failed to load booking details'}
        </Alert>
      </div>
    );
  }

  const isResolvingVariation = requestedVariationId && variationLoading;
  if (isResolvingVariation || (resolvedAllocationId && bookingLoading)) {
    return (
      <div data-testid="booking-details-panel" className="p-6">
        <Stack spacing={2} data-testid="skeleton-loader">
          <Skeleton variant="rectangular" height={72} />
          <Skeleton variant="rectangular" height={112} />
          <Skeleton variant="rectangular" height={180} />
        </Stack>
      </div>
    );
  }

  if (requestedVariationId && variation && !resolvedAllocationId) {
    return (
      <div data-testid="booking-details-panel" className="p-6">
        <Stack
          direction="row"
          alignItems="flex-start"
          spacing={1.5}
          className="pb-2.5"
        >
          <div className="min-w-0 flex-1">
            <Text variant="h6" className="leading-tight">
              {variation.workTypeName}
            </Text>
            <Text variant="body2" color="secondary" className="mt-1.5">
              {variation.description}
            </Text>
          </div>
          <VariationStatusBadge status={variation.status} />
        </Stack>

        <Alert severity="info" className="mb-2.5">
          This variation has not produced a booking yet. Approval actions will
          appear once it is booked.
        </Alert>

        {variation.financialSummary && (
          <BookingSection title="Claim">
            <Text variant="body2" color="secondary">
              Requested amount
            </Text>
            <Text variant="h5" className="mt-0.5 font-semibold">
              {formatCurrency(variation.financialSummary.amount)}
            </Text>
          </BookingSection>
        )}

        <BookingSection title="Evidence">
          <Text variant="body2" className="mb-2.25 whitespace-pre-wrap">
            {variation.notes || 'No work notes added'}
          </Text>
          <Text variant="body2" color="secondary" className="mb-2">
            Photos
          </Text>
          <VariationImageGallery
            variationRequestId={requestedVariationId}
            minimal
          />
        </BookingSection>

        <BookingSection title="Details">
          <InfoRow label="Site" testId="location-site">
            {variation.plotContext.siteName}
          </InfoRow>
          <InfoRow label="Plot" testId="location-plot">
            {variation.plotContext.plotName}
          </InfoRow>
          <InfoRow label="Plot number" testId="location-plot-number">
            {variation.plotContext.plotNumber}
          </InfoRow>
          <InfoRow label="Requested by" testId="booked-by">
            {variation.requestedBy}
          </InfoRow>
          <InfoRow label="Requested" testId="booked-date">
            {formatDate(variation.bookedAt)}
          </InfoRow>
        </BookingSection>

        <BookingActivitySection timeline={variationTimeline} />
      </div>
    );
  }

  if (!booking || !resolvedAllocationId) {
    return (
      <div data-testid="booking-details-panel" className="p-6">
        <Alert severity="info">Booking details are unavailable.</Alert>
      </div>
    );
  }

  const hasCancellationPermission = booking.isVariation
    ? canAdminCancel
    : canCancel || canAdminCancel;
  const cancellationAllowed = canAdminCancelAllocation({
    allocationStatus: booking.status,
    hasPotSubmission: booking.hasPotSubmission,
    potStatus: props.potStatus,
  });
  const canCancelBooking = hasCancellationPermission && cancellationAllowed;
  const hasApprovalActions = booking.status === 'Booked' && canApprove;
  const hasRejectedCancellation =
    booking.status === 'Rejected' && canCancelBooking;
  const hasFooterActions = hasApprovalActions || hasRejectedCancellation;
  const showHeaderCancellation = canCancelBooking && !hasFooterActions;
  const cancellationLocked =
    hasCancellationPermission &&
    !cancellationAllowed &&
    booking.status !== 'Cancelled';

  const openCancellation = () => {
    setReviewReturnsToChooser(false);
    setReviewMode('cancel');
  };
  const openRejection = () => {
    setReviewReturnsToChooser(canCancelBooking);
    setReviewMode(canCancelBooking ? 'resolve' : 'reject');
  };

  if (reviewMode) {
    return (
      <AllocationReviewPanelContent
        allocationId={resolvedAllocationId}
        itemName={
          booking.isVariation
            ? (booking.variation?.workTypeDisplayName ?? 'Variation')
            : (booking.labour?.name ?? 'Labour booking')
        }
        workerName={booking.worker.name}
        amount={booking.amountClaimed}
        isVariation={booking.isVariation}
        returnsQuantity={
          booking.quantityClaimed != null && booking.unitLabel != null
        }
        mode={reviewMode}
        onModeChange={setReviewMode}
        onBack={() =>
          setReviewMode(
            (reviewMode === 'reject' || reviewMode === 'cancel') &&
              reviewReturnsToChooser
              ? 'resolve'
              : null
          )
        }
        onComplete={() => {
          setReviewMode(null);
          setReviewReturnsToChooser(false);
        }}
        onSuccess={props.onReviewSuccess}
      />
    );
  }

  const uniqueReference = booking.uniqueReference;
  const referenceControl = uniqueReference ? (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      spacing={0.5}
    >
      <Text as="span" variant="body2" className="font-semibold">
        {uniqueReference}
      </Text>
      <Tooltip
        content={
          copyState === 'copied'
            ? 'Copied'
            : copyState === 'error'
              ? 'Copy failed'
              : 'Copy reference'
        }
      >
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => handleCopy(uniqueReference)}
          data-testid="copy-reference-button"
          aria-label="Copy booking reference"
        >
          {copyState === 'copied' ? (
            <CheckIcon
              className="size-4 text-content-success"
              aria-hidden="true"
            />
          ) : copyState === 'error' ? (
            <ErrorOutlineIcon
              className="size-4 text-content-error"
              aria-hidden="true"
            />
          ) : (
            <ContentCopyIcon className="size-4" aria-hidden="true" />
          )}
        </Button>
      </Tooltip>
    </Stack>
  ) : null;

  return (
    <div
      data-testid="booking-details-panel"
      className="flex min-h-full flex-col"
    >
      <div className="flex-1 px-6 pt-6">
        <BookingHeader
          booking={booking}
          onCancel={showHeaderCancellation ? openCancellation : undefined}
          cancellationLocked={cancellationLocked}
        />
        <BookingDecisionOutcome booking={booking} />
        <BookingClaimSection booking={booking} />
        <BookingEvidenceSection booking={booking}>
          {booking.isVariation && booking.variation ? (
            <VariationImageGallery
              variationRequestId={booking.variation.id}
              minimal
            />
          ) : (
            <FeatureSwitchGate
              feature={Features.Bookings.IsBookingImagesEnabled}
            >
              <PermissionGate
                permission={Permissions.Labour.BookingImages.Read}
              >
                <BookingImageGallery
                  allocationId={resolvedAllocationId}
                  minimal
                />
              </PermissionGate>
            </FeatureSwitchGate>
          )}
        </BookingEvidenceSection>
        <BookingContextSection
          booking={booking}
          referenceControl={referenceControl}
        />
        <BookingActivitySection timeline={allocationTimeline} />
      </div>

      {hasFooterActions && (
        <div
          data-testid="booking-action-bar"
          className="sticky bottom-0 shrink-0 border-t border-border-default bg-surface-default p-5"
        >
          {hasApprovalActions ? (
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlineDanger"
                fullWidth
                onClick={openRejection}
                data-testid="reject-allocation-button"
              >
                <CancelIcon className="size-4" aria-hidden="true" />
                Reject
              </Button>
              <Button
                variant="success"
                fullWidth
                onClick={() => setReviewMode('approve')}
                data-testid="approve-allocation-button"
              >
                <CheckCircleIcon className="size-4" aria-hidden="true" />
                Approve
              </Button>
            </Stack>
          ) : (
            <Button
              variant="outlineDanger"
              fullWidth
              onClick={openCancellation}
              data-testid="cancel-rejected-allocation-button"
            >
              <CancelIcon className="size-4" aria-hidden="true" />
              Cancel booking
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
